import { z } from 'zod';
import { Currency } from '../enums.js';
import { AutoTopupSettingsSchema, BillingHistoryItemSchema, BillingMode, BillingPaymentState, BillingSmsRecipientMode, BillingWarnRuleSchema, CardSummarySchema, TopupOrderStatus, } from '../billing/schemas.js';
import { IncludedPreparation } from '../billing/preparation.js';
/**
 * Prepaid billing callables (doc 42 §13). Same rule as every other contract:
 * the browser sends ids and intent — never a price, seconds, a customer or a
 * card. `requestId` is a dedupe key, never an authorisation.
 */
const BusinessIdOnly = z.object({ businessId: z.string().min(1) });
/** A client-generated dedupe key for one user action (UUID-ish). */
const RequestId = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);
export const BillingPackageView = z.object({
    packageId: z.string(),
    currency: Currency,
    seconds: z.number().int(),
    /** Stripe list price (net) for display; the quote is what is charged. */
    unitAmountMinor: z.number().int().nullable(),
    taxBehavior: z.enum(['exclusive', 'inclusive', 'unspecified']).nullable(),
});
export const GetBillingOverviewRequest = BusinessIdOnly;
export const GetBillingOverviewResponse = z.object({
    mode: BillingMode,
    currency: Currency,
    catalogVersion: z.string(),
    packages: z.array(BillingPackageView),
    balanceSeconds: z.number().int(),
    reservedSeconds: z.number().int(),
    /** For new calls now: balance − reservations, an ended period counting 0. */
    availableSeconds: z.number().int(),
    /** Subscription allowance (doc 59 N07). Expires at `periodEnd`. */
    included: z.object({
        offerVersion: z.string().nullable(),
        perPeriodSeconds: z.number().int(),
        remainingSeconds: z.number().int(),
        reservedSeconds: z.number().int(),
        periodStart: z.string().nullable(),
        periodEnd: z.string().nullable(),
        status: z.enum(['none', 'active', 'ended']),
        pendingPeriodStart: z.string().nullable(),
        /** Server-decided state of the included minutes (doc 61 R04). Optional
         *  only so a portal build can meet an older Functions deploy. */
        preparation: IncludedPreparation.optional(),
        /** When `scheduled` / `next_renewal` take effect. */
        nextAt: z.string().nullable().optional(),
        /** When the server last checked the subscription invoice. */
        checkedAt: z.string().nullable().optional(),
    }),
    /** Purchased credit (signed; negative = debt). Never expires. */
    purchasedSeconds: z.number().int(),
    paymentState: BillingPaymentState,
    activeOrder: z
        .object({
        orderId: z.string(),
        purpose: z.enum(['manual', 'auto', 'checkout']),
        status: TopupOrderStatus,
        grossMinor: z.number().int(),
        hostedInvoiceUrl: z.string().nullable(),
        expiresAt: z.string().nullable(),
    })
        .nullable(),
    entitlement: z.object({
        kind: z.enum(['ok', 'grace', 'blocked']),
        reason: z.string().nullable(),
        deadline: z.string().nullable(),
    }),
    platform: z.object({
        unitAmountMinor: z.number().int().nullable(),
        taxBehavior: z.enum(['exclusive', 'inclusive', 'unspecified']).nullable(),
    }),
    card: CardSummarySchema.nullable(),
    autoTopup: AutoTopupSettingsSchema,
    autoConsent: z.object({ revision: z.number().int(), termsVersion: z.string(), at: z.string() }).nullable(),
    termsVersion: z.string(),
    period: z.object({
        periodKey: z.string(),
        /** Auto top-ups only, gross. */
        autoSpentMinor: z.number().int(),
        autoPendingMinor: z.number().int(),
        /** Manual + auto paid this month, gross (pending excluded). */
        topupPaidMinor: z.number().int(),
        usedSeconds: z.number().int(),
        startsAt: z.string(),
    }),
    limits: z.object({ cooldownSeconds: z.number().int(), manualOrderTtlMinutes: z.number().int() }),
});
/** Put a business that is about to subscribe on the included-minutes offer
 *  (doc 63). Idempotent; the server decides eligibility. */
export const PrepareActivationRequest = BusinessIdOnly;
export const PrepareActivationResponse = z.object({ outcome: z.string(), offerVersion: z.string().nullable() });
/** The owner's "check again" for included minutes (doc 61 R04): asks the
 *  server to re-read the subscription's latest invoice, at most once a minute
 *  per business. `checked: false` = a check ran moments ago; nothing new. */
export const CheckIncludedMinutesRequest = BusinessIdOnly;
export const CheckIncludedMinutesResponse = z.object({ checked: z.boolean() });
export const QuoteMinuteTopupRequest = z.object({
    businessId: z.string().min(1),
    packageId: z.string().min(1).max(40),
});
export const QuoteMinuteTopupResponse = z.object({
    quoteId: z.string(),
    packageId: z.string(),
    currency: Currency,
    seconds: z.number().int(),
    netMinor: z.number().int(),
    taxMinor: z.number().int(),
    grossMinor: z.number().int(),
    expiresAt: z.string(),
    /** False when Stripe needs a billing address before it can price tax. */
    taxComplete: z.boolean(),
});
export const CreateMinuteTopupRequest = z.object({
    businessId: z.string().min(1),
    quoteId: z.string().min(1).max(80),
    requestId: RequestId,
    returnOrigin: z.string().url(),
});
export const TopupOrderView = z.object({
    orderId: z.string(),
    status: TopupOrderStatus,
    hostedInvoiceUrl: z.string().nullable(),
    expiresAt: z.string().nullable(),
});
export const CreateMinuteTopupResponse = TopupOrderView;
export const SyncMinuteTopupRequest = z.object({ businessId: z.string().min(1), orderId: z.string().min(1).max(80) });
export const SyncMinuteTopupResponse = TopupOrderView.extend({ balanceSeconds: z.number().int() });
export const CancelMinuteTopupRequest = SyncMinuteTopupRequest;
export const CancelMinuteTopupResponse = TopupOrderView;
export const CreateBillingPaymentSetupRequest = z.object({
    businessId: z.string().min(1),
    returnOrigin: z.string().url(),
});
export const CreateBillingPaymentSetupResponse = z.object({ url: z.string().url() });
export const CompleteBillingPaymentSetupRequest = z.object({
    businessId: z.string().min(1),
    sessionId: z.string().min(1).max(200),
});
export const CompleteBillingPaymentSetupResponse = z.object({ card: CardSummarySchema });
export const SaveAutoTopupSettingsRequest = z.object({
    businessId: z.string().min(1),
    enabled: z.boolean(),
    packageId: z.string().min(1).max(40),
    triggerSeconds: z.number().int().min(1).max(10 * 24 * 3600),
    monthlyGrossCapMinor: z.number().int().min(0).max(100_000_000).nullable(),
    /** The gross the owner saw and agreed to for one purchase. */
    acceptedGrossPerPurchaseMinor: z.number().int().min(0).max(100_000_000),
    /** Required when enabling: the terms version the owner accepted. */
    acceptedTermsVersion: z.string().max(40).nullable(),
    expectedRevision: z.number().int().min(0),
});
export const SaveAutoTopupSettingsResponse = z.object({
    revision: z.number().int(),
    /** True when the balance is already below the threshold — a top-up was queued. */
    topupQueued: z.boolean(),
});
export const SaveBillingSmsSettingsRequest = z.object({
    businessId: z.string().min(1),
    enabled: z.boolean(),
    recipientMode: BillingSmsRecipientMode,
    /** Raw national or international input; normalised to E.164 server-side. */
    customPhone: z.string().max(32),
    /** ISO-2 country of the custom number's selector. */
    customPhoneCountry: z.string().length(2),
    first: BillingWarnRuleSchema,
    second: BillingWarnRuleSchema,
    onTopup: z.boolean(),
    expectedRevision: z.number().int().min(0),
});
export const SaveBillingSmsSettingsResponse = z.object({
    revision: z.number().int(),
    customPhone: z.string(),
});
export const ListBillingHistoryRequest = z.object({
    businessId: z.string().min(1),
    /** Opaque cursor from the previous page. */
    cursor: z.string().max(200).nullable().default(null),
    limit: z.number().int().min(1).max(50).default(20),
});
export const BillingHistoryItemView = BillingHistoryItemSchema.omit({ createdAt: true, updatedAt: true }).extend({
    createdAt: z.string(),
});
export const ListBillingHistoryResponse = z.object({
    items: z.array(BillingHistoryItemView),
    nextCursor: z.string().nullable(),
});
/** Safe, owner-facing error codes the callables raise in `details.code`. */
export const BillingErrorCode = z.enum([
    'not_prepaid',
    'billing_disabled',
    'quote_expired',
    'quote_mismatch',
    'tax_location_required',
    'order_active',
    'not_entitled',
    'revision_conflict',
    'validation',
    'no_payment_method',
    'consent_required',
    'price_changed',
    'rate_limited',
]);
//# sourceMappingURL=billing.js.map